import { PrimitiveAtom, atom } from 'jotai';
import DashboardAPI from '../API/DashboardAPI';
import AnalyticsUniqueCustomers from '../Models/Dashboard/Reporting/AnalyticsUniqueCustomers';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import TopSellingProduct from '../Models/Dashboard/Reporting/TopSellingProduct';
import {
    AnalyticsPageMetrics,
    AnalyticsEventMetrics,
    AnalyticsStoreLinkMetrics,
    AnalyticsDailyStoreLinkMetrics,
    AnalyticsDailyPageViewMetrics,
    AnalyticsOrderMetrics, NewUserCount,
} from '../Models/Dashboard/Analytics';

export const AnalyticsStartDateAtom = atom<Date>(new Date());
export const AnalyticsEndDateAtom = atom<Date>(new Date());
export const TopSellingProductListAtom = atom<TopSellingProduct[]>([]);

export const AnalyticsPageMetricsAtom = atom<AnalyticsPageMetrics>({});
export const AnalyticsEventMetricsAtom = atom<AnalyticsEventMetrics>({});
export const AnalyticsStoreLinkMetricsAtom = atom<AnalyticsStoreLinkMetrics>({});
export const AnalyticsDailyStoreLinkMetricsAtom = atom<AnalyticsDailyStoreLinkMetrics>({});
export const AnalyticsDailyPageViewMetricsAtom = atom<AnalyticsDailyPageViewMetrics>({});
export const AnalyticsNewUserCountsAtom = atom<NewUserCount[]>([]);
export const AnalyticsMonthlyPageViewMetricsAtom = atom<AnalyticsDailyPageViewMetrics>({});
export const AnalyticsOrderMetricsAtom = atom<AnalyticsOrderMetrics>({
    averageOrderValue: 0,
    averageItemsPerOrder: 0,
    totalOrders: 0,
    totalRevenue: 0,
});

export const AnalyticsOrderDataAtom = atom<unknown>({});
export const AnalyticsLoggedInCustomersAtom = atom<AnalyticsUniqueCustomers>(new AnalyticsUniqueCustomers());
const analyticsSessionStorage = { ...createJSONStorage(() => sessionStorage), delayInit: false };

export const AnalyticsIdAtom = atomWithStorage('sessionId', '', analyticsSessionStorage);
export const AnalyticsUserProxyAtom = atomWithStorage('userProxy', '');

export const FetchAnalyticsOrderDataAtom = createFetchAnalyticsAtom(
    AnalyticsOrderDataAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsOrderData(null, start, end)).data);

export const FetchTopSellingProductsAtom = createFetchAnalyticsAtom(
    TopSellingProductListAtom,
    async (start, end) => (await DashboardAPI.getTopSellingProducts(null, start, end, "500")).response.products);

export const FetchAnalyticsLoggedInCustomersAtom = createFetchAnalyticsAtom(
    AnalyticsLoggedInCustomersAtom,
    async (start, end) => (await DashboardAPI.getUniqueLoggedInCustomers(null, start, end)).response);

export const FetchAnalyticsPageMetricsAtom = createFetchAnalyticsAtom(
    AnalyticsPageMetricsAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsPageMetrics(null, start, end)).data);

export const FetchAnalyticsEventMetricsAtom = createFetchAnalyticsAtom(
    AnalyticsEventMetricsAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsEventMetrics(null, start, end)).data);

export const FetchAnalyticsStoreLinkMetricsAtom = createFetchAnalyticsAtom(
    AnalyticsStoreLinkMetricsAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsStoreLinkMetrics(null, start, end)).data);

export const FetchAnalyticsDailyStoreLinkMetricsAtom = createFetchAnalyticsAtom(
    AnalyticsDailyStoreLinkMetricsAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsStoreLinkMetricsByTimespan(null, start, end, 'daily')).data);

export const FetchAnalyticsDailyPageViewMetricsAtom = createFetchAnalyticsAtom(
    AnalyticsDailyPageViewMetricsAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsPageViewMetricsByTimespan(null, start, end, 'daily')).data);

export const FetchAnalyticsNewUserCountsAtom = createFetchAnalyticsAtom(
    AnalyticsNewUserCountsAtom,
    async (start, end) => (await DashboardAPI.getNewUserCount(null, start, end)));

export const FetchAnalyticsMonthlyPageViewMetricsAtom = createFetchAnalyticsAtom(
    AnalyticsMonthlyPageViewMetricsAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsPageViewMetricsByTimespan(null, start, end, 'monthly')).data);

export const FetchAnalyticsOrderMetricsAtom = createFetchAnalyticsAtom(
    AnalyticsOrderMetricsAtom,
    async (start, end) => (await DashboardAPI.getAnalyticsOrderMetrics(null, start, end)).data);

function createFetchAnalyticsAtom<T>(DataAtom: PrimitiveAtom<T>, fetchData: (start: Date, end: Date) => Promise<T>) {
    return atom(
        get => get(DataAtom),
        async (get, set) => {
            const start = get(AnalyticsStartDateAtom);
            const end = get(AnalyticsEndDateAtom);
            set(DataAtom, await fetchData(start, end));
        },
    );
}
